import { TableBody } from "react-aria-components";
import { useMemo } from "react";

import type { CosignatureQueueResponse } from "@/core/types";
import { EmptyTable } from "@/ui2/molecules/(table)/EmptyTable";
import { Table2 } from "@/ui2/atoms/(table)/Table";
import { useTableSort } from "@/lib/hooks/useTableSort";

import type { AdaptedQueueRow } from "./helpers/adapters";
import { adaptQueueData } from "./helpers/adapters";
import { CosignatureTableHeader } from "./components/CosignatureTableHeader";
import { CosignatureTableRow } from "./components/CosignatureTableRow";
import { CosignatureTableSkeleton } from "./components/CosignatureTableSkeleton";
import { useCosignatureSelection } from "./hooks/useCosignatureSelection";

type CosignatureTableProps = {
  data: CosignatureQueueResponse | undefined;
  isLoading: boolean;
  onClearFilters?: VoidFunction;
};

// stable reference — prevents useMemo in useTableSort from recomputing every render
const KEY_MAP = { dateSigned: "signatureDate" } satisfies Partial<
  Record<string, keyof AdaptedQueueRow>
>;

export function CosignatureTable({ data, isLoading, onClearFilters }: CosignatureTableProps) {
  const rows = useMemo(() => adaptQueueData(data ?? []), [data]);

  const { onSortChange, sortDescriptor, sortedRows } = useTableSort<AdaptedQueueRow>(rows, {
    initialSortDescriptor: { column: "dateSigned", direction: "descending" },
    keyMap: KEY_MAP,
  });

  const { disabledKeys, onSelectionChange, selectedKeys } = useCosignatureSelection({
    rows,
    sortedRows,
  });

  return (
    <Table2
      aria-label="Co-signature notes"
      disabledKeys={disabledKeys}
      onSelectionChange={onSelectionChange}
      onSortChange={onSortChange}
      selectedKeys={selectedKeys}
      selectionBehavior="toggle"
      selectionMode="multiple"
      sortDescriptor={sortDescriptor}
    >
      <CosignatureTableHeader isDisabled={isLoading} />

      {isLoading ? (
        <CosignatureTableSkeleton />
      ) : (
        <TableBody
          items={sortedRows}
          renderEmptyState={() => (
            <EmptyTable
              ctaProps={onClearFilters && { label: "Clear Filters", onPress: onClearFilters }}
              message="No notes to review"
            />
          )}
        >
          {(row) => <CosignatureTableRow row={row} />}
        </TableBody>
      )}
    </Table2>
  );
}
